import React, { PropsWithChildren } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon, IconType } from '@components';
import { ITheme } from '../../interfaces/styled';

export type ButtonProps = PropsWithChildren<{
  as?: 'a';
  href?: string;
  target?: '_self' | '_blank' | '_parent' | '_top';
  size?: 'sm' | 'md';
  theme?: 'light' | 'dark';
  icon?: IconType;
  title?: string;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}>;

export interface StyledButtonProps {
  height: number;
  columnGap: number;
  textSize: number;
  textWeight: number;
  borderRadius: number;
  padding: string;
  fullWidth?: boolean;
  disabled?: boolean;
  themeStyles?: any;
}

export type IButtonGetSizeMap = (globalTheme: ITheme) => {
  [ key in Exclude<ButtonProps['size'], undefined> ]: any;
};

export type IButtonThemeStylesMap = {
  [ key in Exclude<ButtonProps['theme'], undefined> ]: any;
};

const getSizeMap: IButtonGetSizeMap = (globalTheme) => ({
  sm: {
    textSize: globalTheme.fontSize.xs,
    textWeight: 600,
    borderRadius: 12,
    height: 44,
    padding: '0 16px'
  },
  md: {
    iconSize: 24,
    textSize: globalTheme.fontSize.sm,
    textWeight: 600,
    borderRadius: 12,
    columnGap: 40,
    height: 64,
    padding: '0 24px'
  },
});

const themeStylesMap: IButtonThemeStylesMap = {
  light: css`
    background: #FFFFFF;
    color: #000000;
    
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
    
    &:disabled {
      background: rgba(106, 110, 117, 0.5);
      border: 2px solid rgba(106, 110, 117, 0.2);
      color: rgba(255, 255, 255, 0.4);
    }
  `,
  dark: css`
    background: rgba(106, 110, 117, 0.2);
    border: 1px solid rgba(106, 110, 117, 0.3);
    color: #FFFFFF;
    
    &:hover {
      background: rgba(106, 110, 117, 0.5);
    }
    
    &:disabled {
      background: rgba(106, 110, 117, 0.5);
      border: 1px solid rgba(106, 110, 117, 0.2);
      color: rgba(255, 255, 255, 0.4);
    }
  `
};

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  column-gap: ${({ columnGap }) => columnGap}px;
  width: ${({ fullWidth }) => fullWidth && '100%'};
  height: ${({ height }) => height}px;
  font-family: ${({ theme }) => theme.family};
  font-weight: ${({ textWeight }) => textWeight};
  font-size: ${({ textSize }) => textSize}px;
  line-height: 1;
  text-decoration: none;
  justify-content: center;
  border: 0;
  outline: none;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  box-sizing: border-box;
  padding: ${({ padding }) => padding};
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'all'};
  transition: 0.3s ease-out;
  
  & > ${Icon} {
    margin-left: auto;
  }
  
  ${({ themeStyles }) => themeStyles}
`;

const Button: React.FC<ButtonProps> = (props) => {
  const {
    as,
    href,
    target,
    className,
    size,
    theme,
    icon,
    title,
    type,
    fullWidth,
    disabled,
    onClick,
    children
  } = props;
  
  const globalTheme = useTheme() as ITheme;
  const sizeMap = getSizeMap(globalTheme);
  
  let themeStyles = themeStylesMap.light;
  
  if (theme) {
    themeStyles = themeStylesMap[theme];
  }

  const disabledIconColor = disabled ? 'rgba(255, 255, 255, 0.4)' : undefined;
  
  const defaultSize = sizeMap.md;
  let height = defaultSize.height;
  let iconSize = defaultSize.iconSize;
  let textSize = defaultSize.textSize;
  let textWeight = defaultSize.textWeight;
  let borderRadius = defaultSize.borderRadius;
  let columnGap = defaultSize.columnGap;
  let padding = defaultSize.padding;
  
  if (size) {
    height = sizeMap[size].height;
    iconSize = sizeMap[size].iconSize;
    textSize = sizeMap[size].textSize;
    textWeight = sizeMap[size].textWeight;
    borderRadius = sizeMap[size].borderRadius;
    columnGap = sizeMap[size].columnGap;
    padding = sizeMap[size].padding;
  }
  
  return (
    <StyledButton
      as={as}
      href={href}
      target={target}
      title={title}
      type={type}
      height={height}
      className={className}
      textSize={textSize}
      textWeight={textWeight}
      borderRadius={borderRadius}
      columnGap={columnGap}
      padding={padding}
      themeStyles={themeStyles}
      disabled={disabled}
      fullWidth={fullWidth}
      onClick={onClick}
    >
      {children}
      { icon && <Icon glyph={icon} size={iconSize} color={disabledIconColor} /> }
    </StyledButton>
  )
};

export { Button };
